import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import Stats from "../components/Stats"; 
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import { Factory, Pill, FlaskConical, Package } from "lucide-react";
import { Link } from "react-router-dom"; // or `next/link` if using Next.js
import { ShieldCheck, Lightbulb, Globe, Leaf } from "lucide-react";
export default function Home() {
  const images = [
    "/images/pharma1.webp",
    "/images/pharma2.png",
    "/images/pharma3.png",
  ];

  const quotes = [
    {
      title: "INNOVATING WITH INTEGRITY, DELIVERING WITH EXCELLENCE",
      desc: "We innovate with integrity, ensuring every product reflects our promise of safety, quality, and trust. Excellence isn’t just a goal — it’s our standard.",
    },
    {
      title: "YOUR TRUSTED PARTNER FOR QUALITY AND RELIABILITY.",
      desc: "We are committed to delivering products and services that meet the highest standards, ensuring lasting trust and consistent performance for every client we serve.",
    },
    {
      title: "DRIVEN BY VISION, POWERED BY TRUST.",
      desc: "We move forward with a clear vision for innovation and growth, while building every relationship on the strong foundation of trust and integrity.",
    },
  ];

  const settings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    fade: true,
    arrows: true,
    pauseOnHover: false,
  };

  const foundingYear = 2009;
  const currentYear = new Date().getFullYear();
  const yearsOfExperience = currentYear - foundingYear;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <div className="w-full">
{/* Hero Slider */}
<Slider {...settings}>
  {images.map((src, index) => (
    <div key={index} className="relative">
      <img
    src={src}
    alt={`Pharma Slide ${index + 1}`}
    className="w-full h-[500px] object-cover"
    loading="eager"
  fetchpriority="high"

  />

      {/* Overlay Quote */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center bg-gradient-to-t from-black/70 via-black/30 to-transparent px-6">
        <h2 className="text-white text-2xl md:text-4xl font-bold mb-4">
          {quotes[index].title}
        </h2>
        <p className="text-white text-lg md:text-xl max-w-3xl">
          {quotes[index].desc}
        </p>
      </div>
    </div>
  ))}
</Slider>



      {/* Welcome Section */}
      <section className="p-6 text-center max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-4 text-gray-800">
          Welcome to MAC LABS LIMITED
        </h1>
         <h4 className="text-1xl font-bold mb-4 text-gray-800">
          “Your Trusted Partner in Quality, Innovation & Reliability”
        </h4>
        <p className="text-gray-600 text-lg">
          We are an accredited pharmaceutical manufacturer combining ethical
          business practices with global quality standards. Delivering
          high-quality formulations and APIs since 2009.
        </p>
          <p className="text-gray-600 text-lg">
          MAC Labs Limited has been delivering quality-assured pharmaceutical
          solutions for decades. Our operations comply with global WHO and GMP
          standards, making us a trusted name in both domestic and international
          markets.
        </p>
      </section>
      {/* Who We Are Section */}
      <section ref={ref} className="py-16 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center px-6">
          {/* Left: Experience Number */}
          <div className="text-center md:text-left flex flex-col items-center md:items-start">
            <div className="relative inline-block">
              <h1
                className="text-[220px] md:text-[320px] font-extrabold text-transparent bg-clip-text leading-[1] filter grayscale"
                style={{
                  backgroundImage: "url('/images/building.webp')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                {inView ? (
                  <CountUp start={0} end={yearsOfExperience} duration={2.5} />
                ) : (
                  0
                )}
              </h1>
            </div>

            <motion.p
              className="mt-4 text-xl md:text-2xl font-semibold text-gray-700"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.5 }}
            >
              Years Of Experience in the{" "}
              <span className="text-blue-600">Pharma industry</span>
            </motion.p>
          </div>

          {/* Right: About */}
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              <span className="text-blue-600">Who</span> We Are
            </h2>
            <h3 className="text-lg font-semibold text-blue-600 mb-3">
              Changing The Chemistry of Success Since {foundingYear}
            </h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              MAC Labs Limited was established in {foundingYear}. A leading
              producer of pharmaceutical formulations and APIs with a global
              presence, we deliver high-quality products at competitive prices.
              We value transparency and integrity, ensuring every product meets
              international standards of excellence.
            </p>
           <Link
  to="/contact"
  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md shadow hover:bg-green-700 transition"
>
  Get In Touch →
</Link>

          </div>
        </div>
      </section>
{/* Business Segments */}
<section className="bg-gray-50 py-20 px-6">
  <div className="max-w-6xl mx-auto text-center">
    <h2 className="text-3xl font-bold text-gray-800 mb-12">
      Our <span className="text-blue-600">Business Segments</span>
    </h2>

    <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
      {[
        {
          title: "Contract Manufacturing (CMO/CDMO)",
          icon: <Factory className="w-12 h-12 text-blue-600 mb-4" />,
          desc: "Trusted partner for scalable, quality-assured manufacturing solutions.",
          link: "/business/CMO",
        },
        {
          title: "Active Pharmaceutical Ingredients (APIs)",
          icon: <Pill className="w-12 h-12 text-green-600 mb-4" />,
          desc: "High-quality APIs designed with precision and global compliance.",
          link: "/business/APIs",
        },
        {
          title: "Intermediates & Specialty Chemicals",
          icon: <FlaskConical className="w-12 h-12 text-purple-600 mb-4" />,
          desc: "Innovative intermediates, specialty chemicals & cosmetics ingredients.",
          link: "/business/Intermediates",
        },
        {
          title: "Formulations",
          icon: <Package className="w-12 h-12 text-teal-600 mb-4" />,
          desc: "Advanced formulations delivering safe, effective, and reliable medicines.",
          link: "/business/Formulations",
        },
      ].map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: i * 0.2 }}
          className="flex" // ✅ makes grid item flexible
        >
          <Link
            to={item.link}
            className="h-full flex flex-col items-center text-center p-6 bg-white rounded-2xl shadow hover:shadow-xl hover:scale-105 transition transform duration-300"
          >
            {item.icon}
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {item.title}
            </h3>
            <p className="text-gray-600 text-sm flex-grow">{item.desc}</p>
          </Link>
        </motion.div>
      ))}
    </div>
  </div>
</section>
      {/* Stats */}
      <Stats />
{/* Commitment Section */}
      <section className="relative bg-gray-50 py-10 px-4 sm:py-16 sm:px-6 md:py-20 md:px-8">

  {/* Animated SVG Tiled Background */}
  <svg
    className="absolute inset-0 w-full h-full opacity-10 text-blue-200"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <defs>
      {/* Caution: motion.pattern is not valid. Use normal <pattern> for maximum compatibility. */}
      <pattern
        id="moleculePattern"
        width="120"
        height="120"
        patternUnits="userSpaceOnUse"
      >
        <circle cx="20" cy="20" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <circle cx="100" cy="60" r="8" stroke="currentColor" strokeWidth="1.5" fill="none" />
        <circle cx="60" cy="100" r="6" stroke="currentColor" strokeWidth="1.5" fill="none" />
        {/* Connecting Lines */}
        <line x1="20" y1="20" x2="100" y2="60" stroke="currentColor" strokeWidth="1" />
        <line x1="100" y1="60" x2="60" y2="100" stroke="currentColor" strokeWidth="1" />
        <line x1="60" y1="100" x2="20" y2="20" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#moleculePattern)" />
  </svg>
  {/* Gradient Overlay */}
  <div className="absolute inset-0 bg-gradient-to-b from-blue-50/40 to-gray-50/95 pointer-events-none" />

  {/* Content */}
  <div className="relative max-w-3xl sm:max-w-5xl mx-auto text-center">
    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-6 sm:mb-10">
      Our <span className="text-blue-600">Commitment</span>
    </h2>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
      {[
        {
          title: "Quality First",
          desc: "World-class manufacturing standards with strict compliance.",
          color: "text-blue-600",
          glow: "bg-blue-400/40",
          icon: ShieldCheck,
        },
        {
          title: "Innovation Driven",
          desc: "Continuous R&D for advanced solutions.",
          color: "text-green-600",
          glow: "bg-green-400/40",
          icon: Lightbulb,
        },
        {
          title: "Global Reach",
          desc: "Trusted by clients across continents.",
          color: "text-purple-600",
          glow: "bg-purple-400/40",
          icon: Globe,
        },
        {
          title: "Sustainability Focused",
          desc: "Ethical, safe, and eco-conscious processes.",
          color: "text-teal-600",
          glow: "bg-teal-400/40",
          icon: Leaf,
        },
      ].map((item, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: i * 0.2 }}
          className="flex w-full"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="w-full flex flex-col items-center text-center p-4 sm:p-6 bg-white rounded-2xl shadow hover:shadow-xl transition duration-300"
          >
            {/* Icon + Glowing Aura */}
            <div className="relative mb-3 sm:mb-4 flex items-center justify-center">
              {/* Glowing Ring */}
              <motion.div
                className={`absolute w-14 h-14 sm:w-16 sm:h-16 rounded-full ${item.glow} blur-xl`}
                animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
              {/* Icon */}
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                whileHover={{ scale: 1.2, rotate: 5 }}
                className="relative"
              >
                <item.icon className={`w-10 h-10 sm:w-12 sm:h-12 ${item.color} drop-shadow-md`} aria-label={item.title} />
              </motion.div>
            </div>

            <h3 className={`text-base sm:text-lg md:text-xl font-semibold mb-1 sm:mb-2 ${item.color}`}>
              {item.title}
            </h3>
            <p className="text-gray-600 text-sm sm:text-base flex-grow">{item.desc}</p>
          </motion.div>
        </motion.div>
      ))}
    </div>
  </div>
</section>
{/* Trusted Customers - Marquee Style */}
<section className="relative bg-gradient-to-br from-blue-50 via-white to-gray-50 py-20 px-6 overflow-hidden">
  {/* Decorative subtle pattern */}
  <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_1px_1px,_#3b82f6_1px,_transparent_0)] [background-size:20px_20px]" />

  <div className="relative max-w-6xl mx-auto text-center">
    <h2 className="text-3xl font-bold text-gray-800 mb-4">
      Our <span className="text-blue-600">Trusted Customers</span>
    </h2>
    <p className="text-gray-600 mb-12">
      Partnering with leading pharmaceutical companies across the globe.
    </p>

    {/* Scrolling container */}
    <div className="relative overflow-hidden">
      <div className="flex animate-marquee space-x-12">
        {["hetero.webp", "sunpharma.webp", "zydus.webp"].map((logo, i) => (
          <div key={i} className="min-w-[180px] flex items-center justify-center">
            <img
              src={`/images/${logo}`}
              alt={logo.replace(".webp", "")}
              className="h-20 w-auto object-contain grayscale hover:grayscale-0 transition hover:scale-105"
            />
          </div>
        ))}
        {/* duplicate for seamless loop */}
        {["hetero.webp", "sunpharma.webp", "zydus.webp"].map((logo, i) => (
          <div key={`dup-${i}`} className="min-w-[180px] flex items-center justify-center">
            <img
              src={`/images/${logo}`}
              alt={logo.replace(".webp", "")}
              className="h-20 w-auto object-contain grayscale hover:grayscale-0 transition hover:scale-105"
            />
          </div>
        ))}
      </div>
    </div>
  </div>
</section>
      {/* Contact CTA */}
      <section className="py-12 px-6 text-center bg-blue-50">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Get in Touch</h2>
        <p className="text-gray-600 mb-6">
          Have a query or want to work with us? Contact our team today.
        </p>
        <Link
  to="/contact"
  className="bg-blue-600 text-white px-6 py-3 rounded hover:bg-blue-700 transition"
>
  Contact Us
</Link>
      </section>
    </div>
  );
}
